import { useState } from "react";
import type { TreeNode } from "../lib/canvasTree";

export default function CanvasFileTree({
  nodes,
  activePath,
  entryPath,
  dirtyPaths,
  onOpen,
  onRename,
  onDelete,
}: {
  nodes: TreeNode[];
  activePath: string;
  entryPath: string | null;
  dirtyPaths: Set<string>;
  onOpen(path: string): void;
  onRename(path: string): void;
  onDelete(path: string): void;
}) {
  // Folders start expanded: a canvas is small enough that hiding structure on
  // first open costs more than it saves.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (path: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });

  const render = (list: TreeNode[], depth: number): React.ReactNode =>
    list.map(node =>
      node.kind === "folder" ? (
        <li key={node.path} className="canvas-tree-folder">
          <button
            type="button"
            className="canvas-tree-row"
            style={{ paddingLeft: `${depth * 12 + 8}px` }}
            aria-expanded={!collapsed.has(node.path)}
            onClick={() => toggle(node.path)}
          >
            <span className="canvas-tree-caret">{collapsed.has(node.path) ? "▸" : "▾"}</span>
            <span className="canvas-tree-name">{node.name}</span>
          </button>
          {!collapsed.has(node.path) && <ul>{render(node.children, depth + 1)}</ul>}
        </li>
      ) : (
        <li key={node.path}>
          <div className={`canvas-tree-row canvas-tree-file${node.path === activePath ? " active" : ""}`}>
            <button
              type="button"
              className="canvas-tree-open"
              style={{ paddingLeft: `${depth * 12 + 20}px` }}
              onClick={() => onOpen(node.path)}
              title={node.path}
            >
              <span className="canvas-tree-name">{node.name}</span>
              {node.path === entryPath && <span className="canvas-tree-badge" title="Preview entry point">entry</span>}
              {dirtyPaths.has(node.path) && <span className="canvas-tree-dirty" aria-label="Unsaved changes">●</span>}
            </button>
            <span className="canvas-tree-actions">
              <button type="button" onClick={() => onRename(node.path)} title={`Rename ${node.path}`} aria-label={`Rename ${node.path}`}>✎</button>
              <button type="button" onClick={() => onDelete(node.path)} title={`Delete ${node.path}`} aria-label={`Delete ${node.path}`}>✕</button>
            </span>
          </div>
        </li>
      )
    );

  if (!nodes.length) return <p className="canvas-empty">No files in this revision.</p>;
  return (
    <ul className="canvas-tree" role="tree" aria-label="Canvas files">
      {render(nodes, 0)}
    </ul>
  );
}
